import { RUM_REDACTED_PATH_PARENTS, RUM_SAFE_QUERY_PARAMS, isRumExcludedPath } from './privacyConfig';
export var REDACTED = 'REDACTED';
export var REDACTED_EMAIL = '[email]';
export var REDACTED_PHONE = '[phone]';
/** Same placeholder the SDK uses for a masked click target. */
export var MASKED_ACTION_NAME = 'Masked Element';
var ORIGIN = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i;
// Bounded, and no character class overlaps the separator after it, so matching stays linear on any error text.
var EMAIL = /[a-z0-9._%+-]{1,64}@[a-z0-9-]{1,63}(?:\.[a-z0-9-]{1,63}){1,8}/gi;
var ENCODED_EMAIL = /[a-z0-9._+-]{1,64}%40[a-z0-9-]{1,63}(?:\.[a-z0-9-]{1,63}){1,8}/gi;
var PHONE_CANDIDATE = /(?:\+|%2B)?\d[\d\s().-]{5,}\d/gi;
var ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
var NUMERIC_VALUE = /^[\d.,:_-]*$/;
var LITERAL_VALUE = /^(true|false|null|undefined)$/i;
// Shortest subscriber number we treat as a phone without a leading "+"; DB ids stay well below this.
var MIN_PHONE_DIGITS = 9;
var MIN_PLUS_PHONE_DIGITS = 7;
var countDigits = function (value) { return value.replace(/\D/g, '').length; };
var isPhoneLike = function (value) {
    if (ISO_DATE.test(value))
        return false;
    var digits = countDigits(value);
    var hasPlus = /^(\+|%2B)/i.test(value);
    return hasPlus ? digits >= MIN_PLUS_PHONE_DIGITS : digits >= MIN_PHONE_DIGITS;
};
var safeDecode = function (value) {
    try {
        return decodeURIComponent(value);
    }
    catch (_a) {
        // Malformed escape sequence: judge the raw text instead.
        return value;
    }
};
/** Redacts emails and phone-like strings in free text (error messages, stacks, action names). */
export var scrubText = function (text) {
    try {
        return text
            .replace(EMAIL, REDACTED_EMAIL)
            .replace(ENCODED_EMAIL, REDACTED_EMAIL)
            .replace(PHONE_CANDIDATE, function (match) { return (isPhoneLike(match) ? REDACTED_PHONE : match); });
    }
    catch (_a) {
        // Scrubbing runs inside Datadog's beforeSend and must never throw; send a placeholder, not the raw text.
        return REDACTED;
    }
};
var scrubPath = function (path) {
    var segments = path.split('/');
    return segments
        .map(function (segment, index) {
        if (index > 0 && RUM_REDACTED_PATH_PARENTS.some(function (p) { return p.toLowerCase() === segments[index - 1].toLowerCase(); })) {
            return segment ? REDACTED : segment;
        }
        var decoded = safeDecode(segment);
        var isIdentifying = decoded.includes('@') || (/^[+\d\s().-]+$/.test(decoded) && isPhoneLike(decoded));
        return isIdentifying ? REDACTED : segment;
    })
        .join('/');
};
var scrubQuery = function (query) {
    return query
        .split('&')
        .map(function (pair) {
        var separator = pair.indexOf('=');
        if (separator === -1)
            return pair;
        var name = pair.slice(0, separator);
        var value = safeDecode(pair.slice(separator + 1).replace(/\+/g, ' '));
        var isSafe = RUM_SAFE_QUERY_PARAMS.includes(safeDecode(name)) ||
            LITERAL_VALUE.test(value) ||
            (NUMERIC_VALUE.test(value) && !isPhoneLike(value));
        return isSafe ? pair : "".concat(name, "=").concat(REDACTED);
    })
        .join('&');
};
/**
 * Drops the fragment, redacts free-text query values and identifying path segments. IDs are kept.
 * Never throws: a URL it cannot handle becomes a fixed placeholder.
 */
export var scrubUrl = function (url) {
    var _a, _b;
    try {
        var withoutHash = url.split('#')[0];
        var origin_1 = (_b = (_a = ORIGIN.exec(withoutHash)) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '';
        var rest = withoutHash.slice(origin_1.length);
        var queryStart = rest.indexOf('?');
        if (queryStart === -1)
            return origin_1 + scrubPath(rest);
        return "".concat(origin_1).concat(scrubPath(rest.slice(0, queryStart)), "?").concat(scrubQuery(rest.slice(queryStart + 1)));
    }
    catch (_c) {
        // Same as scrubText: a URL we cannot process is replaced, never sent as-is.
        return REDACTED;
    }
};
var pathnameOf = function (url) {
    var path = url.replace(ORIGIN, '').split(/[?#]/)[0];
    return path || '/';
};
/**
 * Datadog `beforeSend`: drops everything recorded on an excluded route (view events cannot be dropped by
 * the SDK, so those are only sanitised) and scrubs identifying data from what is kept. Fails closed.
 */
export var scrubRumEvent = function (rumEvent) {
    var _a, _b, _c, _d;
    try {
        var event_1 = rumEvent;
        var view = event_1.view, resource = event_1.resource, error = event_1.error, action = event_1.action;
        var isExcluded = isRumExcludedPath(pathnameOf((_a = view === null || view === void 0 ? void 0 : view.url) !== null && _a !== void 0 ? _a : ''));
        if (view === null || view === void 0 ? void 0 : view.url)
            view.url = scrubUrl(view.url);
        if (view === null || view === void 0 ? void 0 : view.referrer)
            view.referrer = scrubUrl(view.referrer);
        if ((_c = (_b = view === null || view === void 0 ? void 0 : view.performance) === null || _b === void 0 ? void 0 : _b.lcp) === null || _c === void 0 ? void 0 : _c.resource_url) {
            view.performance.lcp.resource_url = scrubUrl(view.performance.lcp.resource_url);
        }
        if (isExcluded && event_1.type !== 'view')
            return false;
        if (resource === null || resource === void 0 ? void 0 : resource.url)
            resource.url = scrubUrl(resource.url);
        if (error === null || error === void 0 ? void 0 : error.message)
            error.message = scrubText(error.message);
        if (error === null || error === void 0 ? void 0 : error.stack)
            error.stack = scrubText(error.stack);
        if ((_d = error === null || error === void 0 ? void 0 : error.resource) === null || _d === void 0 ? void 0 : _d.url)
            error.resource.url = scrubUrl(error.resource.url);
        if ((action === null || action === void 0 ? void 0 : action.target) && typeof action.target.name === 'string') {
            action.target.name = scrubText(action.target.name).trim() || MASKED_ACTION_NAME;
        }
        return true;
    }
    catch (_e) {
        // Fail closed: the SDK sends an event when beforeSend throws, so drop any event we could not scrub.
        return false;
    }
};
